// src/components/Footer.jsx
import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaPhone, FaEnvelope, FaMapMarkerAlt, FaFacebook, FaInstagram, FaTwitter, FaLinkedin, FaYoutube, FaGoogle, FaStar, FaMoon, FaSun } from 'react-icons/fa';
import { API_URL } from '../config';
import './Footer.css';

function Footer() {
  const currentYear = new Date().getFullYear();
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    return saved ? JSON.parse(saved) : false;
  });
  const [showEasterEgg, setShowEasterEgg] = useState(false);

  const toggleDarkMode = () => {
    setIsDarkMode(prev => {
      const newMode = !prev;
      localStorage.setItem('darkMode', JSON.stringify(newMode));
      if (newMode) {
        document.documentElement.classList.add('dark-mode');
        document.body.classList.add('dark-mode');
      } else {
        document.documentElement.classList.remove('dark-mode');
        document.body.classList.remove('dark-mode');
      }
      console.log('🌙 Dark mode toggled:', newMode);
      return newMode;
    });
  };

  // Apply dark mode on mount
  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark-mode');
      document.body.classList.add('dark-mode');
    }
  }, []);
  const clickCountRef = useRef(0);
  const clickTimeoutRef = useRef(null);
  const [siteSettings, setSiteSettings] = useState({
    socialMedia: {},
    business: {}
  });

  useEffect(() => {
    const fetchSiteSettings = async () => {
      try {
        const response = await fetch(`${API_URL}/site-settings`);
        if (response.ok) {
          const data = await response.json();
          setSiteSettings({
            socialMedia: data.socialMedia || {},
            business: data.business || {}
          });
        }
      } catch (error) {
        console.error('Failed to fetch site settings:', error);
      }
    };

    fetchSiteSettings();
  }, []);

  const handleDesignerClick = (e) => {
    e.preventDefault();
    clickCountRef.current += 1;

    if (clickTimeoutRef.current) clearTimeout(clickTimeoutRef.current);

    clickTimeoutRef.current = setTimeout(() => {
      clickCountRef.current = 0;
    }, 1500);

    if (clickCountRef.current >= 5) {
      setShowEasterEgg(true);
      clearTimeout(clickTimeoutRef.current);
      clickCountRef.current = 0;
    }
  };

  return (
    <footer className="footer">
      <div className="footer-main">
        <div className="footer-container">
          {/* Company Info */}
          <div className="footer-column">
            <h3 className="footer-title">{siteSettings.business.companyName || 'TR Ventilation'}</h3>
            <p className="footer-description">
              {siteSettings.business.description || 'Premium HVAC supplies for all your ductwork needs. Quality products, fast delivery, and expert support.'}
            </p>
            <div className="footer-social">
              {siteSettings.socialMedia.facebook && (
                <a href={siteSettings.socialMedia.facebook} aria-label="Facebook" className="social-link" target="_blank" rel="noopener noreferrer">
                  <FaFacebook />
                </a>
              )}
              {siteSettings.socialMedia.instagram && (
                <a href={siteSettings.socialMedia.instagram} aria-label="Instagram" className="social-link" target="_blank" rel="noopener noreferrer">
                  <FaInstagram />
                </a>
              )}
              {siteSettings.socialMedia.twitter && (
                <a href={siteSettings.socialMedia.twitter} aria-label="Twitter" className="social-link" target="_blank" rel="noopener noreferrer">
                  <FaTwitter />
                </a>
              )}
              {siteSettings.socialMedia.linkedin && (
                <a href={siteSettings.socialMedia.linkedin} aria-label="LinkedIn" className="social-link" target="_blank" rel="noopener noreferrer">
                  <FaLinkedin />
                </a>
              )}
              {siteSettings.socialMedia.youtube && (
                <a href={siteSettings.socialMedia.youtube} aria-label="YouTube" className="social-link" target="_blank" rel="noopener noreferrer">
                  <FaYoutube />
                </a>
              )}
            </div>
          </div>

          {/* Quick Links */}
          <div className="footer-column">
            <h4 className="footer-heading">Quick Links</h4>
            <ul className="footer-links">
              <li><Link to="/">Home</Link></li>
              <li><Link to="/products">Products</Link></li>
              <li><Link to="/about">About Us</Link></li>
              <li><Link to="/contact">Contact</Link></li>
              <li><Link to="/account">My Account</Link></li>
            </ul>
          </div>

          {/* Product Categories */}
          <div className="footer-column">
            <h4 className="footer-heading">Products</h4>
            <ul className="footer-links">
              <li><Link to={`/products?category=${encodeURIComponent('access & inspection')}`}>Access & Inspection</Link></li>
              <li><Link to="/products?category=dampers">Dampers</Link></li>
              <li><Link to={`/products?category=${encodeURIComponent('fans and ancils')}`}>Fans & Ancils</Link></li>
              <li><Link to={`/products?category=${encodeURIComponent('flexible ducting')}`}>Flexible Ducting</Link></li>
              <li><Link to={`/products?category=${encodeURIComponent('grilles and diffusers')}`}>Grilles & Diffusers</Link></li>
              <li><Link to={`/products?category=${encodeURIComponent('spiral duct and fittings')}`}>Spiral Duct & Fittings</Link></li>
              <li><Link to={`/products?category=${encodeURIComponent('support and ancil')}`}>Support & Ancil</Link></li>
              <li><Link to={`/products?category=${encodeURIComponent('tape and sealant')}`}>Tape & Sealant</Link></li>
            </ul>
          </div>

          {/* Contact Info */}
          <div className="footer-column">
            <h4 className="footer-heading">Contact Us</h4>
            <ul className="footer-contact">
              {siteSettings.business.phone && (
                <li>
                  <FaPhone />
                  <a href={`tel:${siteSettings.business.phone.replace(/\s/g, '')}`}>
                    {siteSettings.business.phone}
                  </a>
                </li>
              )}
              {siteSettings.business.email && (
                <li>
                  <FaEnvelope />
                  <a href={`mailto:${siteSettings.business.email}`}>
                    {siteSettings.business.email}
                  </a>
                </li>
              )}
              {siteSettings.business.address && (
                <li>
                  <FaMapMarkerAlt />
                  <span>
                    {Object.values(siteSettings.business.address)
                      .filter(Boolean)
                      .join(", ")}
                  </span>
                </li>
              )}
              {/* Fallback to default values if no settings */}
              {!siteSettings.business.phone && !siteSettings.business.email && !siteSettings.business.address && (
                <>
                  <li>
                    <FaPhone />
                    <a href="tel:01482205358">01482 205358</a>
                  </li>
                  <li>
                    <FaEnvelope />
                    <a href="mailto:sales@trventilation.com">sales@trventilation.com</a>
                  </li>
                  <li>
                    <FaMapMarkerAlt />
                    <span>Kingston upon Hull<br />England, United Kingdom</span>
                  </li>
                </>
              )}
            </ul>
          </div>

          {/* Google Reviews */}
          <div className="footer-column">
            <h4 className="footer-heading">Rate & Review</h4>
            <div className="footer-reviews">
              <p className="footer-review-text">Share your experience and help others discover our quality products and service.</p>
              <div className="footer-stars">
                <FaStar />
                <FaStar />
                <FaStar />
                <FaStar />
                <FaStar />
              </div>
              <a
                href="https://www.google.com/search?q=trventilation&rlz=1C5CHFA_enGB1000GB1000&oq=trventilation"
                target="_blank"
                rel="noopener noreferrer"
                className="google-review-link"
              >
                <FaGoogle className="me-2" />
                Leave a Review
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Footer Bottom */}
      <div className="footer-bottom">
        <div className="footer-container">
          <p>&copy; {currentYear} TR Ventilation. All rights reserved.</p>

          {/* Dark Mode Toggle Button */}
          <button
            className="dark-mode-toggle"
            onClick={toggleDarkMode}
            title={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
            aria-label={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
          >
            {isDarkMode ? <FaSun /> : <FaMoon />}
          </button>

          <div className="footer-bottom-links">
            <Link to="/privacy">Privacy Policy</Link>
            <span className="separator">|</span>
            <Link to="/terms">Terms & Conditions</Link>
            <span className="separator">|</span>
            <Link to="/returns">Returns & Refunds</Link>
            <span className="separator">|</span>
            <a
              href="https://harleywilliams.co.uk"
              target="_blank"
              rel="noopener noreferrer"
              className="designer"
              style={{ cursor: 'pointer', textDecoration: 'none', color: 'inherit' }}
              title="Visit Harley Williams portfolio"
            >
              Designed by Harley Williams
            </a>
          </div>
        </div>
      </div>

      {/* Easter Egg Modal */}
      {showEasterEgg && (
        <div className="easter-egg-overlay" onClick={() => setShowEasterEgg(false)}>
          <div className="easter-egg-modal" role="dialog" aria-modal="true" aria-label="Easter egg" onClick={(e) => e.stopPropagation()}>
            <h2>You found the secret!</h2>
            <p>Thanks for exploring! This website was crafted with passion by Harley Williams.</p>
            <p style={{ fontWeight: 700, marginTop: '15px' }}>Have a great day!</p>
            <button onClick={() => setShowEasterEgg(false)} className="easter-egg-close">Close</button>
          </div>
        </div>
      )}
    </footer>
  );
}

export default Footer;